// useAudioManager.js
import { useState, useEffect, useCallback, useRef } from 'react';

const useAudioManager = (initialMusicVolume = 7, initialSfxVolume = 27) => {
  const [musicVolume, setMusicVolume] = useState(initialMusicVolume);
  const [sfxVolume, setSfxVolume] = useState(initialSfxVolume);
  const [isMuted, setIsMuted] = useState(false);

  const musicAudioRef = useRef(new Audio());
  const sfxAudioRef = useRef(new Audio());

  useEffect(() => {
    musicAudioRef.current.volume = isMuted ? 0 : musicVolume / 100;
    sfxAudioRef.current.volume = isMuted ? 0 : sfxVolume / 100;
  }, [musicVolume, sfxVolume, isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted(prevMuted => !prevMuted);
  }, []);

  const updateMusicVolume = useCallback((volume) => {
    setMusicVolume(volume);
    musicAudioRef.current.volume = isMuted ? 0 : volume / 100;
    if (isMuted && volume > 0) setIsMuted(false);
  }, [isMuted]);

  const updateSfxVolume = useCallback((volume) => {
    setSfxVolume(volume);
    sfxAudioRef.current.volume = isMuted ? 0 : volume / 100;
    if (isMuted && volume > 0) setIsMuted(false);
  }, [isMuted]);

  const playMusic = useCallback((src, loop = true) => {
    const audio = musicAudioRef.current;

    // Evita carregar se o áudio já está tocando a mesma faixa
    if (audio.src !== window.location.origin + src) {
      audio.pause();
      audio.src = src;
      audio.loop = loop;

      audio.onloadedmetadata = () => {
        audio.volume = isMuted ? 0 : musicVolume / 100;
        audio.play().catch((e) => console.error(e));
      };

      audio.load();
    } else {
      if (audio.paused) {
        audio.volume = isMuted ? 0 : musicVolume / 100;
        audio.play().catch((e) => console.error(e));
      }
    }
  }, [musicVolume, isMuted]);

  const playSfx = useCallback((src) => {
    const audio = new Audio(src);
    audio.volume = isMuted ? 0 : sfxVolume / 100;
    audio.play().catch((e) => console.error('Erro ao tocar SFX:', e));
  }, [sfxVolume, isMuted]);

  return {
    musicVolume,
    sfxVolume,
    isMuted,
    toggleMute,
    updateMusicVolume,
    updateSfxVolume,
    playMusic,
    playSfx,
  };
};

export default useAudioManager;
